import { useEffect, useState } from "react";
import { Loader2, Save, Eye, EyeOff, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Layout } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaUpload } from "@/components/site/MediaUpload";
import { InstructorEditor, StudentResultsEditor, FooterEditor, CardsEditor, MediaLibraryEditor } from "@/pages/admin/AdminWebsiteEditorP2";
import { SiteSettingsEditor } from "@/pages/admin/AdminWebsiteEditorP4";
import { toast } from "sonner";

// Which text fields are editable per section (hero handled in its own tab)
const FIELD_SCHEMA = {
  featured: ["title", "subtitle"],
  why: ["eyebrow", "title", "body"],
  popular: ["title", "subtitle"],
  free: ["title", "subtitle"],
  skills: ["eyebrow", "title"],
  new: ["title", "subtitle"],
  how: ["eyebrow", "title"],
  community: ["body", "button_text"],
  portfolio: ["title"],
  reviews: ["title", "subtitle"],
  motivation: ["body"],
  cta: ["title", "body", "button_text", "button_link"],
};
const LABELS = { eyebrow: "Eyebrow / Label", title: "Title", subtitle: "Subtitle", body: "Body Text", button_text: "Button Text", button_link: "Button Link" };
const NO_TEXT_NOTE = { instructor: "Full instructor editor coming in the next phase.", promo_banners: "Edit banners in Admin → Homepage." };

export default function AdminWebsiteEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  const load = () => api.get("/admin/homepage").then(({ data }) => setForm(data));
  useEffect(() => { load(); }, []);
  if (!form) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sections = form.sections || [];
  const content = form.content || {};

  const setContent = (key, field, val) => setForm((f) => ({ ...f, content: { ...f.content, [key]: { ...(f.content?.[key] || {}), [field]: val } } }));
  const setVisible = (i, val) => { const s = [...sections]; s[i] = { ...s[i], visible: val }; set("sections", s); };
  const setDevice = (i, key, val) => { const s = [...sections]; s[i] = { ...s[i], [key]: val }; set("sections", s); };
  const setDesign = (i, key, val) => { const s = [...sections]; s[i] = { ...s[i], design: { ...(s[i].design || {}), [key]: val } }; set("sections", s); };
  const publish = async () => { await api.post("/admin/homepage/publish"); toast.success("Published live!"); };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= sections.length) return;
    const s = [...sections]; [s[i], s[j]] = [s[j], s[i]]; set("sections", s);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", { sections: form.sections, content: form.content }); toast.success("Website changes saved"); await load(); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const saveHero = async () => {
    setSaving(true);
    const heroFields = ["hero_headline", "hero_subheadline", "hero_description", "hero_video", "hero_image",
      "hero_media_size", "hero_height", "hero_poster", "hero_autoplay", "hero_muted", "hero_controls", "hero_aspect",
      "cta_primary_text", "cta_primary_link", "cta_secondary_text", "cta_secondary_link"];
    const payload = { content: { ...form.content, hero: { eyebrow: form.content?.hero?.eyebrow } } };
    heroFields.forEach((k) => { payload[k] = form[k]; });
    try { await api.put("/admin/homepage", payload); toast.success("Hero saved"); await load(); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-bold uppercase tracking-tight"><Layout className="h-7 w-7 text-[#DCA846]" /> Website Editor</h1>
          <p className="mt-1 text-sm text-zinc-400">Manage your homepage sections, order, visibility and text — no code needed.</p>
        </div>
        <a href="/?preview=1" target="_blank" rel="noreferrer"><Button variant="outline" className="border-white/15 text-white" data-testid="website-preview-btn"><Eye className="mr-1 h-4 w-4" /> Preview Draft</Button></a>
        <Button data-testid="website-publish-btn" onClick={publish} className="bg-emerald-500 text-black hover:bg-emerald-400 ml-2">Publish Live</Button>
      </div>

      <Tabs defaultValue="sections" className="mt-6">
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="sections" data-testid="we-tab-sections">Sections</TabsTrigger>
          <TabsTrigger value="hero" data-testid="we-tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="instructor" data-testid="we-tab-instructor">Instructor</TabsTrigger>
          <TabsTrigger value="results" data-testid="we-tab-results">Student Results</TabsTrigger>
          <TabsTrigger value="cards" data-testid="we-tab-cards">Cards</TabsTrigger>
          <TabsTrigger value="media" data-testid="we-tab-media">Media</TabsTrigger>
          <TabsTrigger value="site" data-testid="we-tab-site">Site</TabsTrigger>
          <TabsTrigger value="footer" data-testid="we-tab-footer">Footer</TabsTrigger>
        </TabsList>

        {/* SECTIONS */}
        <TabsContent value="sections" className="mt-5">
          <div className="mb-4 flex justify-end">
            <Button data-testid="save-sections-btn" onClick={save} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Changes</>}
            </Button>
          </div>
          <div className="space-y-2" data-testid="section-manager">
            {sections.map((s, i) => {
              const fields = FIELD_SCHEMA[s.key];
              const isOpen = openKey === s.key;
              return (
                <div key={s.key} data-testid={`section-item-${s.key}`} className={`rounded-lg border ${s.visible === false ? "border-white/5 opacity-60" : "border-white/10"} bg-zinc-900/40`}>
                  <div className="flex items-center gap-2 p-3">
                    <div className="flex flex-col">
                      <button data-testid={`section-up-${s.key}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-zinc-400 hover:text-[#DCA846] disabled:opacity-20"><ArrowUp className="h-4 w-4" /></button>
                      <button data-testid={`section-down-${s.key}`} disabled={i === sections.length - 1} onClick={() => move(i, 1)} className="text-zinc-400 hover:text-[#DCA846] disabled:opacity-20"><ArrowDown className="h-4 w-4" /></button>
                    </div>
                    <button onClick={() => setOpenKey(isOpen ? null : s.key)} className="flex flex-1 items-center gap-2 text-left">
                      {fields ? (isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />) : <span className="w-4" />}
                      <span className="font-display uppercase tracking-wide text-white">{s.label}</span>
                    </button>
                    <div className="flex items-center gap-2">
                      {s.visible === false ? <EyeOff className="h-4 w-4 text-zinc-500" /> : <Eye className="h-4 w-4 text-[#DCA846]" />}
                      <Switch data-testid={`section-visible-${s.key}`} checked={s.visible !== false} onCheckedChange={(v) => setVisible(i, v)} />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 border-t border-white/5 px-3 py-2 text-xs text-zinc-400">
                    <label className="flex items-center gap-1.5">Desktop <Switch data-testid={`section-desktop-${s.key}`} checked={s.desktop !== false} onCheckedChange={(v) => setDevice(i, "desktop", v)} /></label>
                    <label className="flex items-center gap-1.5">Mobile <Switch data-testid={`section-mobile-${s.key}`} checked={s.mobile !== false} onCheckedChange={(v) => setDevice(i, "mobile", v)} /></label>
                    <select data-testid={`section-spacing-${s.key}`} value={s.design?.spacing || "normal"} onChange={(e) => setDesign(i, "spacing", e.target.value)} className="rounded bg-zinc-900 border border-white/10 px-2 py-1 text-white">
                      <option value="compact">Spacing: Compact</option><option value="normal">Spacing: Normal</option><option value="spacious">Spacing: Spacious</option>
                    </select>
                    <select data-testid={`section-bg-${s.key}`} value={s.design?.background || "none"} onChange={(e) => setDesign(i, "background", e.target.value)} className="rounded bg-zinc-900 border border-white/10 px-2 py-1 text-white">
                      <option value="none">BG: None</option><option value="panel">BG: Panel</option>
                    </select>
                    <select data-testid={`section-align-${s.key}`} value={s.design?.align || "left"} onChange={(e) => setDesign(i, "align", e.target.value)} className="rounded bg-zinc-900 border border-white/10 px-2 py-1 text-white">
                      <option value="left">Align: Left</option><option value="center">Align: Center</option>
                    </select>
                  </div>
                  {isOpen && fields && (
                    <div className="space-y-3 border-t border-white/5 p-4">
                      {fields.map((fld) => (
                        <div key={fld}>
                          <Label className="text-xs uppercase tracking-wider text-zinc-400">{LABELS[fld]}</Label>
                          {fld === "body" ? (
                            <Textarea data-testid={`field-${s.key}-${fld}`} rows={3} value={content[s.key]?.[fld] ?? ""} onChange={(e) => setContent(s.key, fld, e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" />
                          ) : (
                            <Input data-testid={`field-${s.key}-${fld}`} value={content[s.key]?.[fld] ?? ""} onChange={(e) => setContent(s.key, fld, e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {isOpen && !fields && (
                    <div className="border-t border-white/5 p-4 text-sm text-zinc-500">{NO_TEXT_NOTE[s.key] || "This section has no editable text — use show/hide and reorder."}</div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-zinc-500">Hidden sections are kept safely and can be re-enabled anytime. Reorder with the up/down arrows.</p>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero" className="mt-5">
          <div className="space-y-4 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
            <F label="Eyebrow Text"><Input data-testid="hero-eyebrow" value={content.hero?.eyebrow ?? ""} onChange={(e) => setContent("hero", "eyebrow", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Main Headline"><Input data-testid="hero-headline" value={form.hero_headline ?? ""} onChange={(e) => set("hero_headline", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Subheadline"><Input data-testid="hero-subheadline" value={form.hero_subheadline ?? ""} onChange={(e) => set("hero_subheadline", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Description"><Textarea rows={3} value={form.hero_description ?? ""} onChange={(e) => set("hero_description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Primary CTA Text"><Input value={form.cta_primary_text ?? ""} onChange={(e) => set("cta_primary_text", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
              <F label="Primary CTA Link"><Input value={form.cta_primary_link ?? ""} onChange={(e) => set("cta_primary_link", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
              <F label="Secondary CTA Text"><Input value={form.cta_secondary_text ?? ""} onChange={(e) => set("cta_secondary_text", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
              <F label="Secondary CTA Link"><Input value={form.cta_secondary_link ?? ""} onChange={(e) => set("cta_secondary_link", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            </div>
            <MediaUpload testid="hero-video-upload" label="Hero 9:16 Video" value={form.hero_video} onChange={(v) => set("hero_video", v)} kind="video" accept="video/*" />
            <MediaUpload label="Hero Fallback Image" value={form.hero_image} onChange={(v) => set("hero_image", v)} kind="image" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="text-xs uppercase text-zinc-400">Media Size</Label>
                <Select value={form.hero_media_size || "medium"} onValueChange={(v) => set("hero_media_size", v)}>
                  <SelectTrigger className="mt-1 bg-zinc-900 border-white/10 text-white" data-testid="hero-media-size"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white"><SelectItem value="small">Small</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="large">Large</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase text-zinc-400">Section Height</Label>
                <Select value={form.hero_height || "standard"} onValueChange={(v) => set("hero_height", v)}>
                  <SelectTrigger className="mt-1 bg-zinc-900 border-white/10 text-white" data-testid="hero-height"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white"><SelectItem value="compact">Compact</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="tall">Tall</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 border-t border-white/5 pt-4">
              <MediaUpload label="Video Poster / Thumbnail" value={form.hero_poster} onChange={(v) => set("hero_poster", v)} kind="image" testid="hero-poster" />
              <div><Label className="text-xs uppercase text-zinc-400">Video Aspect Ratio</Label>
                <Select value={form.hero_aspect || "9:16"} onValueChange={(v) => set("hero_aspect", v)}>
                  <SelectTrigger className="mt-1 bg-zinc-900 border-white/10 text-white" data-testid="hero-aspect"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-white"><SelectItem value="9:16">9:16 (Vertical)</SelectItem><SelectItem value="16:9">16:9 (Wide)</SelectItem><SelectItem value="1:1">1:1 (Square)</SelectItem><SelectItem value="4:5">4:5 (Portrait)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="hero-autoplay" checked={form.hero_autoplay !== false} onCheckedChange={(v) => set("hero_autoplay", v)} /> Autoplay</label>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="hero-muted" checked={form.hero_muted !== false} onCheckedChange={(v) => set("hero_muted", v)} /> Muted</label>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="hero-controls" checked={!!form.hero_controls} onCheckedChange={(v) => set("hero_controls", v)} /> Show Controls</label>
            </div>
            <Button data-testid="save-hero-btn" onClick={saveHero} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Hero</>}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="instructor" className="mt-5"><InstructorEditor /></TabsContent>
        <TabsContent value="results" className="mt-5"><StudentResultsEditor /></TabsContent>
        <TabsContent value="cards" className="mt-5"><CardsEditor /></TabsContent>
        <TabsContent value="media" className="mt-5"><MediaLibraryEditor /></TabsContent>
        <TabsContent value="site" className="mt-5"><SiteSettingsEditor /></TabsContent>
        <TabsContent value="footer" className="mt-5"><FooterEditor /></TabsContent>
      </Tabs>
    </div>
  );
}

function F({ label, children }) { return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1">{children}</div></div>; }
